import { useState, useEffect } from 'react';
import { CheckCircle2, Circle, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useTenantStore } from '@/hooks/useTenantStore';

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  action?: () => void;
  actionLabel?: string;
}

export function OnboardingChecklist() {
  const navigate = useNavigate();
  const { tenantId } = useTenantStore();
  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [items, setItems] = useState<ChecklistItem[]>([
    {
      id: 'company',
      title: 'Company created',
      description: 'Set up your company profile',
      completed: false,
    },
    {
      id: 'shareholders',
      title: 'Add shareholders',
      description: 'Add your first shareholders',
      completed: false,
      action: () => navigate('/shareholders'),
      actionLabel: 'Add Shareholders',
    },
    {
      id: 'shares',
      title: 'Issue shares',
      description: 'Issue shares to your shareholders',
      completed: false,
      action: () => navigate('/transactions'),
      actionLabel: 'Issue Shares',
    },
    {
      id: 'team',
      title: 'Invite team members',
      description: 'Add team members to collaborate',
      completed: false,
      action: () => navigate('/settings?tab=team-access'),
      actionLabel: 'Manage Team',
    },
    {
      id: 'dataroom',
      title: 'Upload documents',
      description: 'Add important company documents',
      completed: false,
      action: () => navigate('/data-room'),
      actionLabel: 'Go to Data Room',
    },
  ]);

  useEffect(() => {
    const savedDismissed = localStorage.getItem('onboarding-checklist-dismissed');
    if (savedDismissed === 'true') {
      setDismissed(true);
    }
    checkProgress();
  }, [tenantId]);

  const checkProgress = async () => {
    if (!tenantId) return;

    try {
      const [
        { count: shareholderCount },
        { count: ledgerCount },
        { count: teamCount },
        { count: fileCount },
      ] = await Promise.all([
        supabase
          .from('shareholders')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', tenantId),
        supabase
          .from('ledger_events')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', tenantId),
        supabase
          .from('user_roles')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', tenantId),
        supabase
          .from('data_room_files')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', tenantId),
      ]);

      setItems((prev) =>
        prev.map((item) => {
          if (item.id === 'company') return { ...item, completed: true };
          if (item.id === 'shareholders')
            return { ...item, completed: (shareholderCount || 0) > 0 };
          if (item.id === 'shares') return { ...item, completed: (ledgerCount || 0) > 0 };
          if (item.id === 'team') return { ...item, completed: (teamCount || 0) > 1 };
          if (item.id === 'dataroom') return { ...item, completed: (fileCount || 0) > 0 };
          return item;
        })
      );
    } catch (error) {
      console.error('Error checking progress:', error);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('onboarding-checklist-dismissed', 'true');
    setDismissed(true);
  };

  if (dismissed || !tenantId) return null;

  const completedCount = items.filter((i) => i.completed).length;
  const progress = (completedCount / items.length) * 100;

  if (progress === 100) return null;

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div className="flex-1">
          <CardTitle className="text-lg flex items-center gap-2">
            Getting Started
            <span className="text-sm font-normal text-muted-foreground">
              {completedCount}/{items.length} completed
            </span>
          </CardTitle>
          <Progress value={progress} className="h-2 mt-2" />
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setExpanded(!expanded)}
            className="h-8 w-8"
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={handleDismiss} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-3 pt-0">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-start gap-3 p-3 rounded-lg bg-background/50 hover:bg-background/80 transition-colors"
            >
              {item.completed ? (
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              ) : (
                <Circle className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p
                      className={`font-medium text-sm ${
                        item.completed ? 'text-muted-foreground line-through' : ''
                      }`}
                    >
                      {item.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                  </div>
                  {!item.completed && item.action && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={item.action}
                      className="flex-shrink-0"
                    >
                      {item.actionLabel}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
}
